import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { NotificationType } from '../../shared/models/notification-type';
import { ErrorHandlerService } from '../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { User } from '../../shared/services/user/models/user.model';
import { UserService } from '../../shared/services/user/user.service';
import { CustomerService } from '../../shared/services/customer/customer.service';
import { ValidationService } from '../../shared/services/validation/validation.service';
import { UserTermsConditionsComponent } from './user-terms-conditions/user-terms-conditions.component';
import { UserTermsUseComponent } from './user-terms-use/user-terms-use.component';
import { SiteConfig } from '../../../config/site-config';
import { CustomerWebHooks } from '../../shared/models/customer/customer.webhooks.model';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
    selector: 'upsc-user-settings',
    templateUrl: './user-settings.component.html',
    styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit {
    public formGroup: UntypedFormGroup;
    public AcceptedDate: string;
    // public showAlert: boolean = false;
    // public customMessage: string;
    public termsConditions: string;
    public termsofUse: string;
    public isInProgress = false;
    // @ViewChild('userSettingsForm') userSettingsForm: any;
    // @ViewChild('userUpdatedInfo') userUpdatedInfo: ElementRef;
    // @ViewChild('userUpdateFailedInfo') userUpdateFailedInfo: ElementRef;
    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();
    public webhooksList: CustomerWebHooks[];    
    public isDataLoading = false;
    public emailstring;
    public noRecordsFound = false;
    public isClassActive = true;

    public constructor(public userService: UserService,
                        public customerService: CustomerService,
                       private notificationService: NotificationService,
                       private formBuild: UntypedFormBuilder,
                       private validationService: ValidationService,
                       private errorHandlerService: ErrorHandlerService,
                       private dialog: MatDialog,
                       private translateService: TranslateService,
                       public user: User,
                       private clipboard: Clipboard
    ) {
        this.getUser();
        this.getTerms();
        this.getWebHooksList();
    }

    public ngOnInit(): void {
        this.formGroup = this.formBuild.group({
            firstName: ['', Validators.compose([Validators.required])],
            lastName: ['', Validators.compose([Validators.required])],
            email: ['', Validators.compose([Validators.required, this.validationService.emailFormatValidator()])],
            isAlwaysNotifySender: [false],
            isAlwaysNotifyRecipient: [false],
            isAlwaysNotifyCustomRecipient: [false],
            isAlwaysUseThermal: [false],
            isAutomaticPrint: [false],
            isAlwaysPrintReceipt: [false],
            isAlwaysPrintHighValue: [false],
            userId: [''],
            termsAcceptedOn: [''],
        });
        this.dtOptions = SiteConfig.tableConfig;
        this.dtTrigger.next(null);
        this.emailstring = "mailto:" + this.translateService.instant('InsuranceTypeAssistanceEmail')
    }

    public getUser(): void {
        this.userService.getUser()
            .pipe(
                catchError((error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorLoadingUserDetail'),
                        NotificationType.ERROR);

                    return of(null);
                }),
            )
            .subscribe(
                (user) => {
                    if (user) {
                        this.populateProperties(user);
                    }
                },
            );
    }

    public getTerms(): void {
        this.userService.getTermsSigned()
            .pipe(
                catchError((error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorLoadingTerms'),
                        NotificationType.ERROR);

                    return of(null);
                }),
            )
            .subscribe(
                (term) => {
                    if (term) {
                        this.AcceptedDate = term.AcceptedDate;
                        this.termsConditions = term.TermsCondition;
                        this.termsofUse = term.TermsUsage;
                    }
                },
            );
    }

    public getWebHooksList() {    
        this.isDataLoading = true;
        this.customerService.getCustomerWebHooks()
          .subscribe(
            {
                next: (webhooks) => {
                    if (webhooks) {
                      this.webhooksList = webhooks || [];
          
                      this.dtTrigger.next(null);
                      this.isDataLoading = false;
                      if(this.webhooksList.length==0)
                      {
                        this.noRecordsFound = true;
                        this.isClassActive = false;
                      }
                      else
                      {
                        this.noRecordsFound = false;
                        this.isClassActive = true;
                      }
                    }
                  },
                error: (error) => {
                    this.dtTrigger.next(null);
                    this.notificationService.notify(
                            this.errorHandlerService.getHttpErrorMessage(error),
                            this.translateService.instant('errorLoadingWebHooksList'),
                            NotificationType.ERROR);
                    this.isDataLoading = false;
                },
            }
        );
      }

    public copyToClipboard(event, text: string) {
        event.preventDefault();  
        this.clipboard.copy(text);
        this.notificationService.notify(
            "Text Copied !","",
            NotificationType.INFO);
    }

    public openTermsOfUseDialog(event: MouseEvent): void {
        event.preventDefault();

        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '70%',
            data: {
                termsofUse: this.termsofUse,
            },
        };

        const dialogRef = this.dialog.open(UserTermsUseComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(() => {
        });
    }

    public openTermsAndConditionsDialog(event: MouseEvent): void {
        event.preventDefault();

        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '70%',
            data: {
                termsConditions: this.termsConditions,
            },
        };

        const dialogRef = this.dialog.open(UserTermsConditionsComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(() => {
        });
    }

    /*onSubmit(event, form) {
      event.preventDefault();
      this.isInProgress = true;
      this.mapFormData(form);
      console.log(form);
      this.userservice.saveUser(this.user).subscribe(
        (response) => {
          // if (response) {
          this.notificationService.notify('', this.translateService.instant('UserSettingsUpdated'), NotificationType.SUCCESS);
          //}
          this.isInProgress = false;
          console.log(2);
        },
        (error) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('UserSettingsFailed'),
            NotificationType.ERROR);
          this.isInProgress = false;
        });
    } */

    public updateUser(): void {
        this.isInProgress = true;
        this.mapFormData();
        this.userService.saveUser(this.user)
            .pipe(
                finalize(() => {
                    this.isInProgress = false;
                }),
                catchError(
                    (error) => {
                        this.notificationService.notify(
                            this.errorHandlerService.getHttpErrorMessage(error),
                            this.translateService.instant('UserSettingsFailed'),
                            NotificationType.ERROR);

                        return of(null);
                    }),
            )
            .subscribe(
                () => {
                    this.notificationService.notify('', this.translateService.instant('UserSettingsUpdated'), NotificationType.SUCCESS);
                    this.isInProgress = false;
                },
            );
    }

    public mapFormData(): void {
        this.user.FirstName = this.formGroup.controls.firstName.value;
        this.user.LastName = this.formGroup.controls.lastName.value;
        this.user.Email = this.formGroup.controls.email.value;
        this.user.AlwaysNotifySender = this.formGroup.controls.isAlwaysNotifySender.value;
        this.user.AlwaysNotifyRecipient = this.formGroup.controls.isAlwaysNotifyRecipient.value;

        // When saving a user setting, we use CustomEmailNotification instead of AlwaysSendCustomEmailToRecipient.
        // However, when retrieving a user, it returns the value in AlwaysSendCustomEmailToRecipient property.
        // TODO: modify the API to make it consistent!
        this.user.CustomEmailNotification = this.formGroup.controls.isAlwaysNotifyCustomRecipient.value;

        this.user.AlwaysUseThermal = this.formGroup.controls.isAlwaysUseThermal.value;
        this.user.AutomaticPrint = this.formGroup.controls.isAutomaticPrint.value;
        this.user.AlwaysPrintReceipt = this.formGroup.controls.isAlwaysPrintReceipt.value;
        this.user.AlwaysPrintHighValue = this.formGroup.controls.isAlwaysPrintHighValue.value;
        this.user.UserId = this.formGroup.controls.userId.value;
        this.user.TermsAcceptedOn = this.formGroup.controls.termsAcceptedOn.value;
    }

    // Getting only essential values or else all the properties get assigned to user object.
    // When we try to save/update back with all the properties, it is not getting saved. rather throwing error.
    // Get only essential values, so that we send only essential values while save/update.
    private populateProperties(userData: User): void {
        this.formGroup.setValue({
            firstName: userData.FirstName,
            lastName: userData.LastName,
            email: userData.Email,
            isAlwaysNotifySender: userData.AlwaysNotifySender || false,
            isAlwaysNotifyRecipient: userData.AlwaysNotifyRecipient || false,
            isAlwaysNotifyCustomRecipient: userData.AlwaysSendCustomEmailToRecipient || false,
            isAlwaysUseThermal: userData.AlwaysUseThermal || false,
            isAutomaticPrint: userData.IsAutoPrint || false,
            isAlwaysPrintReceipt: userData.AlwaysPrintReceipt || false,
            isAlwaysPrintHighValue: userData.AlwaysPrintHighValue || false,
            userId: userData.UserId,
            termsAcceptedOn: userData.TermsAcceptedOn,
        });

        this.user.FirstName = userData.FirstName;
        this.user.LastName = userData.LastName;
        this.user.Email = userData.Email;
        this.user.AlwaysNotifySender = userData.AlwaysNotifySender;
        this.user.AlwaysNotifyRecipient = userData.AlwaysNotifyRecipient;
        this.user.AlwaysSendCustomEmailToRecipient = userData.AlwaysSendCustomEmailToRecipient;
        this.user.CustomEmailNotification = userData.AlwaysSendCustomEmailToRecipient;
        this.user.AlwaysUseThermal = userData.AlwaysUseThermal;
        this.user.AutomaticPrint = userData.IsAutoPrint;
        this.user.AlwaysPrintReceipt = userData.AlwaysPrintReceipt;
        this.user.AlwaysPrintHighValue = userData.AlwaysPrintHighValue;
        this.user.UserId = userData.UserId;
        this.user.TermsAcceptedOn = userData.TermsAcceptedOn;
    }
}
